import { useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getIn, useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import { fetchFerryServices } from 'redux/actions/service';
import { setSegmentStop } from 'redux/actions/ui/sealink';

export const ALL_SUBROUTE_VALUE = 'ALL_SUBROUTE';
export const EMPTY_SUBROUTE_VALUE = 'EMPTY_SUBROUTE';

const getSubrouteOptions = (multiDestination, subroutes) =>
  subroutes.map((item) => ({
    label: multiDestination ? item.toRouteStopName : item.fromRouteStopName,
    value: item.id,
  }));

function isMutipleDestination(subroutes) {
  if (subroutes.length > 1) {
    if (subroutes[0].toRouteStopId !== subroutes[1].toRouteStopId) {
      return true;
    }
  }
  return false;
}

const getRouteStop = (routeStops) => (id) =>
  routeStops.find((item) => item.id === id) || {};

const SubrouteSelect = ({
  value,
  subroutes,
  routeStops,
  onChange,
  segmentId,
  curJourneyIndex,
  allPlacesMapLink,
}) => {
  const dispatch = useDispatch();

  const multiDestination = isMutipleDestination(subroutes);
  const options = getSubrouteOptions(multiDestination, subroutes);
  const getRouteStopT = getRouteStop(routeStops);
  const segments = useSelector((state) => state.sealink.segments);
  const { subroutesLabel } = segments[segmentId] || {};

  const handleSelectChange = (event) => {
    onChange(event.target.value);
  };

  useLayoutEffect(() => {
    if (value === EMPTY_SUBROUTE_VALUE) {
      return;
    }

    let result;
    if (value === ALL_SUBROUTE_VALUE) {
      result = { departureRouteStop: {}, arrivalRouteStop: {} };
    } else {
      const val = Number.parseInt(value);
      const subroute = subroutes.find((item) => item.id === val);
      result = {
        departureRouteStop: getRouteStopT(subroute.fromRouteStopId),
        arrivalRouteStop: getRouteStopT(subroute.toRouteStopId),
      };
    }

    dispatch(setSegmentStop(segmentId, curJourneyIndex, result));
    dispatch(fetchFerryServices(segmentId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, segmentId, curJourneyIndex]);

  useLayoutEffect(() => {
    if (subroutes.length === 1) {
      onChange(subroutes[0].id.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subroutes]);

  const label =
    subroutesLabel || (multiDestination ? 'destinations' : 'pick-up points');
  const labelOfAll = subroutes.length > 2 ? `All ${label}` : `Both ${label}`;
  const dropdownLabel = `Filter by ${pluralize(label, 1)}`;

  const link = {
    href: allPlacesMapLink,
    label,
  };
  const val = Number.parseInt(value);
  const subroute = subroutes.find((item) => item.id === val);
  if (!isEmpty(subroute)) {
    link.href = subroute.googleMapLink;
    link.label = multiDestination
      ? subroute.toRouteStopName
      : subroute.fromRouteStopName;
  }

  // Only have one subroute, select it by default and hide dropdown
  if (subroutes.length === 1) {
    return null;
  }

  return (
    <div>
      <label className="form-label w-100">
        {dropdownLabel}
        <select
          className="form-control form-select"
          onChange={handleSelectChange}
          value={value}
        >
          <option value={ALL_SUBROUTE_VALUE}>{labelOfAll}</option>
          {options.map(({ label: optionLabel, value: optionValue }) => (
            <option value={optionValue} key={optionValue}>
              {optionLabel}
            </option>
          ))}
        </select>
      </label>
      {link.href && (
        <div className="text-x-small pt-1">
          <a target="_blank" rel="noreferrer" href={link.href}>
            View {link.label} in Google Maps
          </a>
        </div>
      )}
    </div>
  );
};

export default SubrouteSelect;

export const subrouteType = {
  id: PropTypes.number,
  fromRouteStopId: PropTypes.number,
  fromRouteStopName: PropTypes.string,
  toRouteStopId: PropTypes.number,
  toRouteStopName: PropTypes.string,
  googleMapLink: PropTypes.string,
};

SubrouteSelect.propTypes = {
  value: PropTypes.string,
  subroutes: PropTypes.arrayOf(PropTypes.shape(subrouteType)).isRequired,
  routeStops: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })),
  onChange: PropTypes.func.isRequired,
  segmentId: PropTypes.number,
  curJourneyIndex: PropTypes.number,
  allPlacesMapLink: PropTypes.string,
};

export const SubrouteSelectWithFormik = ({
  productTypeId,
  direction,
  ...props
}) => {
  const { setFieldValue, values } = useFormikContext();
  const fieldName = `${productTypeId}.subrouteId.${direction}`;
  const onChange = (v) => {
    setFieldValue(fieldName, v);
  };
  return (
    <SubrouteSelect
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      value={getIn(values, fieldName)}
      onChange={onChange}
    />
  );
};

SubrouteSelectWithFormik.propTypes = {
  direction: PropTypes.oneOf(['departure', 'return']).isRequired,
  productTypeId: PropTypes.number.isRequired,
};
